// chartData.ts — the pure mapping from a metric's series to MetricPage's chart
// points, including the rule that marks a point as a regression.
//
// A point regressed when it moved the wrong way relative to the previous point:
// up for a lower_is_better metric, down for a higher_is_better one. The first
// point has no predecessor, so its delta is 0 and it never regresses. Equal
// consecutive values are not regressions in either direction. Kept free of
// React and recharts so the rule can be unit-tested (chartData.test.ts).
//
// Dirty points (a dirty mark says they are not comparable) never take part in
// the trend: a dirty point has delta 0 and never regresses, and the next clean
// point is compared against the previous CLEAN one, so a leaked spike neither
// regresses nor becomes the bar the following commit falls short of.
import type { DirtyRef, SeriesResponse } from '../api';

export interface ChartPoint {
  index: number;
  sha: string;
  short: string;
  value: number;
  message: string;
  author: string;
  authored_at?: string;
  delta: number; // signed difference from the previous clean value
  regressed: boolean;
  dirty: boolean;
  dirtyMarks?: DirtyRef[];
}

// buildChartData turns a series response into ordered chart points. A null
// response (still loading) maps to an empty chart.
export function buildChartData(data: SeriesResponse | null): ChartPoint[] {
  if (!data) return [];
  let prev: number | null = null;
  const direction = data.metric.direction;
  const points = data.points.map((p, i): ChartPoint => {
    const dirty = !!p.dirty;
    const delta = prev === null || dirty ? 0 : p.value - prev;
    const regressed = !dirty && prev !== null && (direction === 'lower_is_better'
      ? delta > 0
      : delta < 0);
    if (!dirty) prev = p.value;
    return {
      index: i,
      sha: p.sha,
      short: p.sha.slice(0, 8),
      value: p.value,
      message: (p.message.split('\n')[0] || '(no message)').slice(0, 20),
      author: p.author_name,
      authored_at: p.authored_at,
      delta,
      regressed,
      dirty,
      dirtyMarks: p.dirty_marks,
    };
  });
  console.debug(
    `[increment] chartData: metric=${data.metric.name} direction=${direction} ` +
    `points=${points.length} regressions=${points.filter((p) => p.regressed).length}`,
  );
  return points;
}
